import React from 'react';
import CampaignLaunchGuidePanel from './CampaignLaunchGuidePanel';
import {
  CAMPAIGN_LAUNCH_GUIDE_HASH,
  CAMPAIGN_PURPOSE_FIELD_ID,
  campaignNextStepCopy,
  campaignNextStepSectionDocs,
  campaignPurposeCopy,
} from './campaignLaunchGuide';
import {
  CAMPAIGN_NEXT_STEP_LIMITS,
  CAMPAIGN_NEXT_STEP_LIMIT_STORAGE_KEY,
  CAMPAIGN_SELECTED_STORAGE_KEY,
  assessCampaignReadinessFromLive,
  parseCampaignNextStepLimit,
  type CampaignNextStepLimit,
  type CampaignReadinessAction,
} from './campaignReadiness';
import HelpBadge from './components/HelpBadge';
import CrmTermText, { CrmTermHelp } from './CrmTermText';
import { type CrmGlossaryTermId } from './crmGlossary';

type Project = {
  slug: string;
  name: string;
  status?: string;
  icp?: string | null;
  description?: string | null;
  website?: string | null;
  protected?: boolean;
};

type Product = {
  id: number;
  name: string;
  sku?: string | null;
  family?: string | null;
  list_price: number;
};

type AudienceCriterion = { dimension: string; value: string };
type CampaignAudience = { product?: string | null; description?: string | null; criteria?: AudienceCriterion[]; matrix?: Array<{ key: string; label: string; value: string; source?: string | null }> };
type Campaign = {
  id: number;
  name: string;
  channel: string;
  status: string;
  budget: number;
  notes?: string | null;
  audience?: CampaignAudience | null;
};
type Quote = { id: number; name: string; status: string; amount: number };
type Ticket = { id: number; subject: string; status: string; priority: string; account_id?: number | null };
type Sequence = { id: number; name: string; channel: string; steps?: string | null };
type SequenceEnrollment = {
  id: number;
  sequence_id: number;
  lead_id?: number | null;
  contact_id?: number | null;
  status: string;
  step_index?: number;
};
type Opportunity = { id: number; name: string; product?: string | null };
type Lead = { tags?: string | null; status?: string };
type Account = { id: number; name: string };
type ReportPayload = {
  won_amount?: number;
  win_rate?: number;
  lead_conversion_rate?: number;
  open_tickets?: number;
  avg_meddpicc?: number;
  overdue_next_action?: number;
  missing_next_action?: number;
};

type PipelineStage = { stage: string; count: number; amount: number };

type Props = {
  view: string;
  spanish: boolean;
  busy: boolean;
  project: string;
  projects: Project[];
  products: Product[];
  campaigns: Campaign[];
  leads: Lead[];
  quotes: Quote[];
  tickets: Ticket[];
  sequences: Sequence[];
  enrollments: SequenceEnrollment[];
  reports: ReportPayload | null;
  accounts: Account[];
  opportunities: Opportunity[];
  pipelineStages: PipelineStage[];
  overdueCount: number;
  missingNextCount: number;
  openLeadCount: number;
  openDealCount: number;
  openAmount: number;
  openTaskCount: number;
  websiteLeadCount: number;
  enrollmentCount: number;
  money: (value: number | null | undefined) => string;
  accountName: (id: number) => string;
  setProject: (slug: string) => void;
  setView: (view: string) => void;
  onOpenPipeline: (dealView?: string) => void;
  createFromForm: (path: string, body: Record<string, unknown>) => void;
  runMutate: (work: () => Promise<void>) => void;
  callApi: (path: string, init?: RequestInit) => Promise<unknown>;
  crmDataRevision: number;
  leadStats?: { by_campaign?: Record<string, number> };
};

const PursuitProjectsPanel: React.FC<{
  spanish: boolean;
  busy: boolean;
  project: string;
  projects: Project[];
  setProject: (slug: string) => void;
  setView: (view: string) => void;
  runMutate: (work: () => Promise<void>) => void;
  callApi: (path: string, init?: RequestInit) => Promise<unknown>;
}> = ({ spanish, busy, project, projects, setProject, setView, runMutate, callApi }) => {
  const [filter, setFilter] = React.useState<'active' | 'archived'>('active');
  const [editor, setEditor] = React.useState<null | { mode: 'create' } | { mode: 'edit'; slug: string }>(null);
  const [pendingArchive, setPendingArchive] = React.useState<Project | null>(null);
  const [draftName, setDraftName] = React.useState('');
  const [draftSlug, setDraftSlug] = React.useState('');
  const [draftDescription, setDraftDescription] = React.useState('');
  const [draftIcp, setDraftIcp] = React.useState('');
  const [draftWebsite, setDraftWebsite] = React.useState('');

  const visible = projects.filter((row) => (filter === 'archived' ? row.status === 'archived' : row.status !== 'archived'));

  const openEditor = (target: { mode: 'create' } | { mode: 'edit'; slug: string }) => {
    setPendingArchive(null);
    if (target.mode === 'create') {
      setDraftName('');
      setDraftSlug('');
      setDraftDescription('');
      setDraftIcp('');
      setDraftWebsite('');
    } else {
      const row = projects.find((item) => item.slug === target.slug);
      setDraftName(row?.name || '');
      setDraftSlug(row?.slug || '');
      setDraftDescription(row?.description || '');
      setDraftIcp(row?.icp || '');
      setDraftWebsite(row?.website || '');
    }
    setEditor(target);
  };

  const saveEditor = () => {
    const name = draftName.trim();
    if (!name || !editor) return;
    void runMutate(async () => {
      if (editor.mode === 'create') {
        const created = (await callApi('/api/v1/pursuit/projects', {
          method: 'POST',
          body: JSON.stringify({
            name,
            slug: draftSlug.trim() || null,
            description: draftDescription.trim() || null,
            icp: draftIcp.trim() || null,
            website: draftWebsite.trim() || null,
          }),
        })) as Project;
        if (created?.slug) setProject(created.slug);
      } else {
        await callApi(`/api/v1/pursuit/projects/${editor.slug}`, {
          method: 'PATCH',
          body: JSON.stringify({
            name,
            description: draftDescription.trim() || null,
            icp: draftIcp.trim() || null,
            website: draftWebsite.trim() || null,
          }),
        });
      }
      setEditor(null);
    });
  };

  return (
    <div className="pursuit-project-studio">
      <div className="pursuit-project-toolbar">
        <div>
          <h3>
            {spanish ? 'Espacios de trabajo' : 'Workspaces'}
            <HelpBadge label={spanish ? 'Acerca de los espacios' : 'About workspaces'}>
              <p>
                {spanish
                  ? 'Un espacio es una organización que vende (este es NextCompli). En HubSpot o Pipedrive, “Projects” son entrega post-venta. CompliCore, CompliAdvantage y Enterprise son productos del trato — no cree un espacio por plan. Archive en vez de borrar.'
                  : 'A workspace is a selling organization (this one is NextCompli). In HubSpot or Pipedrive, “Projects” are post-sale delivery. CompliCore, CompliAdvantage, and Enterprise are products on deals — do not create a workspace per plan. Archive instead of delete so history stays intact.'}
              </p>
            </HelpBadge>
          </h3>
        </div>
        <div className="pursuit-segmented" role="tablist" aria-label={spanish ? 'Estado del espacio' : 'Workspace status'}>
          <button type="button" role="tab" className={filter === 'active' ? 'is-active' : undefined} onClick={() => setFilter('active')}>
            {spanish ? 'Activos' : 'Active'}
          </button>
          <button type="button" role="tab" className={filter === 'archived' ? 'is-active' : undefined} onClick={() => setFilter('archived')}>
            {spanish ? 'Archivados' : 'Archived'}
          </button>
        </div>
        <button type="button" className="dashboard-btn dashboard-btn-primary" onClick={() => openEditor({ mode: 'create' })}>
          {spanish ? 'Nuevo espacio' : 'New workspace'}
        </button>
      </div>

      {editor ? (
        <form
          className="pursuit-project-editor"
          onSubmit={(event) => {
            event.preventDefault();
            saveEditor();
          }}
        >
          <h4>{editor.mode === 'create' ? (spanish ? 'Nuevo espacio' : 'New workspace') : (spanish ? 'Editar espacio' : 'Edit workspace')}</h4>
          <label>
            {spanish ? 'Nombre' : 'Name'}
            <input value={draftName} onChange={(event) => setDraftName(event.target.value)} required autoFocus />
          </label>
          {editor.mode === 'create' ? (
            <label>
              Slug
              <input value={draftSlug} onChange={(event) => setDraftSlug(event.target.value)} placeholder="partner-reseller" />
            </label>
          ) : (
            <p className="pursuit-project-slug">{editor.slug}</p>
          )}
          <label>
            {spanish ? 'Para quién vendemos' : 'Who this book is for'}
            <input value={draftIcp} onChange={(event) => setDraftIcp(event.target.value)} placeholder={spanish ? 'A quién vende este libro…' : 'Who this book sells to…'} />
          </label>
          <label>
            {spanish ? 'Notas' : 'Notes'}
            <input value={draftDescription} onChange={(event) => setDraftDescription(event.target.value)} />
          </label>
          <label>
            Website
            <input value={draftWebsite} onChange={(event) => setDraftWebsite(event.target.value)} placeholder="https://" />
          </label>
          <div className="pursuit-project-editor-actions">
            <button type="submit" className="dashboard-btn dashboard-btn-primary" disabled={busy || !draftName.trim()}>
              {spanish ? 'Guardar' : 'Save'}
            </button>
            <button type="button" className="dashboard-btn" onClick={() => setEditor(null)}>
              {spanish ? 'Cancelar' : 'Cancel'}
            </button>
          </div>
        </form>
      ) : null}

      {pendingArchive ? (
        <div className="pursuit-confirm-bar" role="alertdialog" aria-labelledby="pursuit-archive-title">
          <p id="pursuit-archive-title">
            {spanish
              ? `¿Archivar ${pendingArchive.name}? El pipeline se oculta, no se borra.`
              : `Archive ${pendingArchive.name}? The pipeline is hidden, not deleted.`}
          </p>
          <button
            type="button"
            className="dashboard-btn dashboard-btn-primary"
            disabled={busy}
            onClick={() =>
              void runMutate(async () => {
                await callApi(`/api/v1/pursuit/projects/${pendingArchive.slug}`, { method: 'DELETE' });
                if (project === pendingArchive.slug) {
                  const next = projects.find((item) => item.slug !== pendingArchive.slug && item.status !== 'archived');
                  if (next) setProject(next.slug);
                }
                setPendingArchive(null);
              })
            }
          >
            {spanish ? 'Archivar' : 'Archive'}
          </button>
          <button type="button" className="dashboard-btn" onClick={() => setPendingArchive(null)}>
            {spanish ? 'Cancelar' : 'Cancel'}
          </button>
        </div>
      ) : null}

      {visible.length === 0 ? (
        <div className="pursuit-empty">
          <p>
            {filter === 'archived'
              ? spanish
                ? 'No hay espacios archivados.'
                : 'No archived workspaces.'
              : spanish
                ? 'Aún no hay un espacio de ventas. Cree el libro NextCompli.'
                : 'No sales workspaces yet. Create the NextCompli book.'}
          </p>
          {filter === 'active' ? (
            <button type="button" className="dashboard-btn-primary" onClick={() => openEditor({ mode: 'create' })}>
              {spanish ? 'Nuevo espacio' : 'New workspace'}
            </button>
          ) : null}
        </div>
      ) : (
        <ul className="pursuit-project-grid">
          {visible.map((row) => {
            const current = row.slug === project;
            return (
              <li key={row.slug} className={`pursuit-project-card${current ? ' is-current' : ''}${row.status === 'archived' ? ' is-archived' : ''}`}>
                <div className="pursuit-project-card-head">
                  <strong>{row.name}</strong>
                  <span className={`pursuit-stage-pill${row.protected ? ' is-protected' : ''}`}>
                    {row.protected ? (spanish ? 'Protegido' : 'Protected') : row.status === 'archived' ? (spanish ? 'Archivado' : 'Archived') : (spanish ? 'Activo' : 'Active')}
                  </span>
                </div>
                <code>{row.slug}</code>
                <p>{row.icp || row.description || (spanish ? 'Sin notas' : 'No notes yet')}</p>
                <div className="pursuit-project-card-actions">
                  {row.status === 'archived' ? (
                    <button
                      type="button"
                      className="dashboard-btn"
                      disabled={busy}
                      onClick={() =>
                        void runMutate(async () => {
                          await callApi(`/api/v1/pursuit/projects/${row.slug}`, {
                            method: 'PATCH',
                            body: JSON.stringify({ status: 'active' }),
                          });
                          setProject(row.slug);
                          setFilter('active');
                        })
                      }
                    >
                      {spanish ? 'Restaurar' : 'Restore'}
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="dashboard-btn dashboard-btn-primary"
                        onClick={() => {
                          setProject(row.slug);
                          setView('pipeline');
                        }}
                      >
                        {spanish ? 'Abrir tablero' : 'Open board'}
                      </button>
                      <button type="button" className="dashboard-btn" onClick={() => openEditor({ mode: 'edit', slug: row.slug })}>
                        {spanish ? 'Editar' : 'Edit'}
                      </button>
                      {row.protected ? null : (
                        <button type="button" className="dashboard-btn" disabled={busy} onClick={() => setPendingArchive(row)}>
                          {spanish ? 'Archivar' : 'Archive'}
                        </button>
                      )}
                    </>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

const CAMPAIGN_CHANNELS = [
  { value: 'website', label: 'Website' },
  { value: 'email', label: 'Email' },
  { value: 'outbound', label: 'Outbound' },
  { value: 'event', label: 'Event' },
] as const;

const CampaignPurposeField: React.FC<{
  spanish: boolean;
  name?: string;
  value?: string;
  showHelp?: boolean;
  onChange?: (value: string) => void;
  onBlur?: () => void;
}> = ({ spanish, name, value, showHelp, onChange, onBlur }) => {
  const copy = campaignPurposeCopy(spanish);
  return (
    <label className="pursuit-prose-field">
      <span>
        {copy.label}
        {showHelp ? (
          <HelpBadge label={copy.helpLabel}>
            <p>{copy.help}</p>
          </HelpBadge>
        ) : null}
      </span>
      <textarea
        id={onChange ? CAMPAIGN_PURPOSE_FIELD_ID : undefined}
        name={name}
        rows={3}
        aria-label={copy.label}
        {...(onChange
          ? { value: value || '', onChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => onChange(event.target.value) }
          : {})}
        onBlur={onBlur}
        placeholder={copy.placeholder}
      />
    </label>
  );
};

const PursuitCampaignsPanel: React.FC<{
  spanish: boolean;
  busy: boolean;
  campaigns: Campaign[];
  products: Product[];
  leads: Lead[];
  opportunities: Opportunity[];
  sequenceCount: number;
  openTaskCount: number;
  websiteLeadCount: number;
  quoteCount: number;
  setView: (view: string) => void;
  onOpenPipeline: (dealView?: string) => void;
  runMutate: (work: () => Promise<void>) => void;
  callApi: (path: string, init?: RequestInit) => Promise<unknown>;
  createFromForm: (path: string, body: Record<string, unknown>) => void;
  crmDataRevision: number;
  leadStats?: { by_campaign?: Record<string, number> };
}> = ({
  spanish,
  busy,
  campaigns,
  products,
  leads,
  opportunities,
  sequenceCount,
  openTaskCount,
  websiteLeadCount,
  quoteCount,
  setView,
  onOpenPipeline,
  runMutate,
  callApi,
  createFromForm,
  crmDataRevision,
  leadStats,
}) => {
  const [selectedId, setSelectedId] = React.useState<number | null>(() => {
    const stored = typeof window === 'undefined' ? '' : window.localStorage.getItem(CAMPAIGN_SELECTED_STORAGE_KEY);
    const parsed = Number(stored);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  });
  const selected = campaigns.find((row) => row.id === selectedId) || null;
  const [draftName, setDraftName] = React.useState(selected?.name || '');
  const [draftChannel, setDraftChannel] = React.useState(selected?.channel || 'outbound');
  const [draftStatus, setDraftStatus] = React.useState(selected?.status || 'planned');
  const [draftBudget, setDraftBudget] = React.useState(String(selected?.budget ?? 0));
  const [draftNotes, setDraftNotes] = React.useState(selected?.notes || '');
  const [draftProduct, setDraftProduct] = React.useState(selected?.audience?.product || '');
  const [draftDescription, setDraftDescription] = React.useState(selected?.audience?.description || '');
  const [pendingDelete, setPendingDelete] = React.useState(false);
  const [creating, setCreating] = React.useState(false);
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [nextStepLimit, setNextStepLimit] = React.useState<CampaignNextStepLimit>(() =>
    parseCampaignNextStepLimit(
      typeof window === 'undefined' ? undefined : window.localStorage.getItem(CAMPAIGN_NEXT_STEP_LIMIT_STORAGE_KEY),
    ),
  );

  React.useEffect(() => {
    setDraftName(selected?.name || '');
    setDraftChannel(selected?.channel || 'outbound');
    setDraftStatus(selected?.status || 'planned');
    setDraftBudget(String(selected?.budget ?? 0));
    setDraftNotes(selected?.notes || '');
    setDraftProduct(selected?.audience?.product || '');
    setDraftDescription(selected?.audience?.description || '');
    setPendingDelete(false);
  }, [selected]);

  React.useEffect(() => {
    setSettingsOpen(false);
  }, [selectedId]);

  const patchCampaign = (body: Record<string, unknown>) => {
    if (!selected) return;
    void runMutate(async () => {
      await callApi(`/api/v1/pursuit/campaigns/${selected.id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
    });
  };

  const saveCampaign = () => {
    if (!selected || !draftName.trim()) return;
    patchCampaign({
      name: draftName.trim(),
      channel: draftChannel,
      status: draftStatus,
      budget: Number(draftBudget || 0),
      notes: draftNotes.trim() || null,
      audience: {
        product: draftProduct || null,
        description: draftDescription.trim() || '',
        criteria: [],
        matrix: selected.audience?.matrix || [],
      },
    });
  };

  const saveCampaignPurpose = () => {
    if (!selected) return;
    const notes = draftNotes.trim() || null;
    if ((notes || '') === String(selected.notes || '').trim()) return;
    patchCampaign({ notes });
  };

  const inboundLabel = spanish ? 'Entrante' : 'Inbound';
  const categoryTiles = React.useMemo(() => {
    const planOrder = ['CompliCore', 'CompliAdvantage', 'Enterprise'];
    const used = new Set<number>();
    const tiles: Array<{ campaign: Campaign; category: string }> = [];
    planOrder.forEach((plan) => {
      campaigns
        .filter((row) => row.audience?.product === plan)
        .forEach((campaign) => {
          used.add(campaign.id);
          tiles.push({ campaign, category: plan });
        });
    });
    campaigns
      .filter((row) => !row.audience?.product)
      .forEach((campaign) => {
        used.add(campaign.id);
        tiles.push({ campaign, category: inboundLabel });
      });
    campaigns
      .filter((row) => !used.has(row.id))
      .forEach((campaign) => {
        tiles.push({ campaign, category: campaign.audience?.product || inboundLabel });
      });
    return tiles;
  }, [campaigns, inboundLabel]);

  const categoryGroups = React.useMemo(() => {
    const groups: Array<{ category: string; campaigns: Campaign[] }> = [];
    categoryTiles.forEach(({ campaign, category }) => {
      const existing = groups.find((group) => group.category === category);
      if (existing) existing.campaigns.push(campaign);
      else groups.push({ category, campaigns: [campaign] });
    });
    return groups;
  }, [categoryTiles]);

  const product = String(selected?.audience?.product || '').trim();
  const readiness = React.useMemo(() => {
    if (!selected) return null;
    return assessCampaignReadinessFromLive({
      channel: selected.channel,
      status: selected.status,
      notes: selected.notes,
      audience: selected.audience,
      leads,
      opportunities,
      sequenceCount,
      openTaskCount,
      websiteLeadCount,
      quoteCount,
      nextLimit: nextStepLimit,
      campaignLeadCount: selected ? leadStats?.by_campaign?.[String(selected.id)] : undefined,
    });
  }, [selected, leads, opportunities, sequenceCount, openTaskCount, websiteLeadCount, quoteCount, nextStepLimit, crmDataRevision, leadStats]);

  const setShownNextSteps = (limit: CampaignNextStepLimit) => {
    setNextStepLimit(limit);
    window.localStorage.setItem(CAMPAIGN_NEXT_STEP_LIMIT_STORAGE_KEY, String(limit));
  };

  const runNextStep = (action: CampaignReadinessAction) => {
    if (action === 'purpose') {
      document.getElementById(CAMPAIGN_PURPOSE_FIELD_ID)?.focus();
      return;
    }
    if (action === 'settings') {
      setSettingsOpen(true);
      return;
    }
    if (action === 'activate') {
      if (!selected) return;
      setDraftStatus('active');
      void runMutate(async () => {
        await callApi(`/api/v1/pursuit/campaigns/${selected.id}`, {
          method: 'PATCH',
          body: JSON.stringify({ status: 'active' }),
        });
      });
      return;
    }
    if (action === 'generate' || action === 'leads') {
      setView('leads');
      return;
    }
    if (action === 'sequences') {
      setView('sequences');
      return;
    }
    if (action === 'tasks') {
      setView('tasks');
      return;
    }
    if (action === 'website') {
      setView('website');
      return;
    }
    if (action === 'quotes') {
      setView('quotes');
      return;
    }
    onOpenPipeline('all');
  };

  const openLaunchGuide = (hash: string) => {
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}#${hash}`);
    setView('guide');
  };

  return (
    <div className="pursuit-campaign-studio">
      <div className="pursuit-project-toolbar">
        <h3>
          {spanish ? 'Campañas' : 'Campaigns'}
          <HelpBadge label={spanish ? 'Acerca de las campañas' : 'About campaigns'}>
            <p>
              {spanish
                ? 'Elija una campaña. Luego describa el ICP en Leads, revise la matriz, y busque leads (o cree uno a mano con el mismo SKU).'
                : 'Choose a campaign. Describe the ICP on Leads, review the matrix, then search for matching leads (or add one by hand with the same SKU).'}
            </p>
          </HelpBadge>
        </h3>
        <button type="button" className="dashboard-btn" onClick={() => setCreating((open) => !open)}>
          {creating ? (spanish ? 'Cancelar' : 'Cancel') : spanish ? 'Nueva campaña' : 'New campaign'}
        </button>
      </div>
      {creating ? (
      <form
        className="pursuit-form"
        onSubmit={(event) => {
          event.preventDefault();
          const form = new FormData(event.currentTarget);
          createFromForm('/api/v1/pursuit/campaigns', {
            name: String(form.get('name') || '').trim(),
            channel: String(form.get('channel') || 'website'),
            status: 'planned',
            budget: Number(form.get('budget') || 0),
            notes: String(form.get('notes') || '').trim() || null,
            audience: { product: null, description: '', criteria: [] },
          });
          event.currentTarget.reset();
          setCreating(false);
        }}
      >
        <input
          name="name"
          aria-label={spanish ? 'Nombre de campaña' : 'Campaign name'}
          placeholder={spanish ? 'Nueva campaña' : 'New campaign'}
          required
        />
        <select name="channel" aria-label={spanish ? 'Canal' : 'Channel'} defaultValue="outbound">
          {CAMPAIGN_CHANNELS.map((channel) => (
            <option key={channel.value} value={channel.value}>
              {channel.label}
            </option>
          ))}
        </select>
        <input name="budget" type="number" min={0} aria-label={spanish ? 'Presupuesto' : 'Budget'} placeholder="Budget" />
        <CampaignPurposeField spanish={spanish} name="notes" />
        <button type="submit" className="dashboard-security-inline-btn" disabled={busy}>
          {spanish ? 'Añadir' : 'Add'}
        </button>
      </form>
      ) : null}
      {campaigns.length === 0 ? (
        <div className="pursuit-empty">
          <p>{spanish ? 'No hay campañas.' : 'No campaigns yet.'}</p>
        </div>
      ) : (
        <div className="pursuit-campaign-studio-body">
          <label className="pursuit-campaign-picker">
            {spanish ? 'Campaña' : 'Campaign'}
            <select
              aria-label={spanish ? 'Elegir campaña' : 'Choose a campaign'}
              value={selectedId ?? ''}
              onChange={(event) => {
                const nextId = event.target.value ? Number(event.target.value) : null;
                setSelectedId(nextId);
                if (nextId) window.localStorage.setItem(CAMPAIGN_SELECTED_STORAGE_KEY, String(nextId));
                else window.localStorage.removeItem(CAMPAIGN_SELECTED_STORAGE_KEY);
              }}
            >
              <option value="">{spanish ? 'Elegir…' : 'Choose…'}</option>
              {categoryGroups.map((group) => (
                <optgroup key={group.category} label={group.category}>
                  {group.campaigns.map((campaign) => (
                    <option key={campaign.id} value={campaign.id}>
                      {campaign.name}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </label>
          {selected && readiness ? (
            <div className="pursuit-project-editor">
              <h4>{selected.name}</h4>
              <CampaignPurposeField
                spanish={spanish}
                showHelp
                value={draftNotes}
                onChange={setDraftNotes}
                onBlur={saveCampaignPurpose}
              />
              <div className="pursuit-campaign-head">
                <section
                  className="pursuit-readiness"
                  aria-label={spanish ? 'Preparación de campaña' : 'Campaign readiness'}
                >
                  <div className="pursuit-readiness-ring" aria-hidden="true">
                    <svg viewBox="0 0 36 36">
                      <circle className="pursuit-readiness-track" cx="18" cy="18" r="15.9155" />
                      <circle
                        className="pursuit-readiness-fill"
                        cx="18"
                        cy="18"
                        r="15.9155"
                        pathLength="100"
                        strokeDasharray={`${readiness.percent} 100`}
                        transform="rotate(-90 18 18)"
                      />
                    </svg>
                    <strong>{readiness.percent}%</strong>
                  </div>
                  <div>
                    <h5>
                      {spanish ? 'Preparación de campaña' : 'Campaign readiness'}
                      <HelpBadge label={spanish ? 'Qué mide este porcentaje' : 'What this percentage means'}>
                        <p>
                          {spanish
                            ? 'Es la parte de la lista de lanzamiento hecha: SKU, audiencia, notas, estado activo, un lead y un trato. Los próximos pasos rellenan huecos con trabajo de seguimiento.'
                            : 'This is the share of the launch checklist done: SKU, audience, notes, active status, a lead, and a deal. Immediate next steps then fill leftover slots with follow-on work.'}
                        </p>
                      </HelpBadge>
                    </h5>
                    <p role="status">
                      {spanish
                        ? `${readiness.done} de ${readiness.total} listos`
                        : `${readiness.done} of ${readiness.total} complete`}
                    </p>
                  </div>
                </section>
                <div className="pursuit-next-steps" role="region" aria-label={spanish ? 'Próximos pasos' : 'Immediate next steps'}>
                  <div className="pursuit-next-steps-toolbar">
                    <h5>
                      {spanish ? 'Próximos pasos' : 'Immediate next steps'}
                      <HelpBadge label={spanish ? 'Acerca de los próximos pasos' : 'About immediate next steps'}>
                        <p>
                          {spanish
                            ? 'La lista se llena con pasos de lanzamiento abiertos. Cuando cierra uno, entra el siguiente, incluido el seguimiento (inscribir en secuencia, primera tarea, primera cotización) para que la ventana 3 o 5 no quede vacía. Tener Discovery follow-up en el catálogo no cierra el paso de secuencia. Tras Actualizar, Pursuit vuelve a leer la campaña, los leads y los tratos.'
                            : 'This list fills with open launch steps first. When you finish one, the next open step takes its place — including follow-on work (enroll in a sequence, first task, first quote) so a 3 or 5 window stays populated. A seed sequence in the catalog does not close the sequence step. Refresh re-reads the campaign, leads, and deals.'}
                        </p>
                      </HelpBadge>
                    </h5>
                    <div
                      className="pursuit-next-step-limit"
                      role="radiogroup"
                      aria-label={spanish ? 'Cuántos pasos mostrar' : 'How many next steps to show'}
                    >
                      {CAMPAIGN_NEXT_STEP_LIMITS.map((limit) => (
                        <button
                          key={limit}
                          type="button"
                          role="radio"
                          aria-checked={nextStepLimit === limit}
                          className={nextStepLimit === limit ? 'is-selected' : undefined}
                          onClick={() => setShownNextSteps(limit)}
                        >
                          {limit}
                        </button>
                      ))}
                    </div>
                  </div>
                  <p className="pursuit-next-step-fill" role="status">
                    {spanish
                      ? `Mostrando ${readiness.next.length} de ${nextStepLimit}. Quedan ${readiness.openLaunch} de lanzamiento y ${readiness.openFollowOn} de seguimiento.`
                      : `Showing ${readiness.next.length} of ${nextStepLimit}. ${readiness.openLaunch} launch and ${readiness.openFollowOn} follow-on still open.`}
                  </p>
                  {readiness.next.length ? (
                    <ul>
                      {readiness.next.map((row) => {
                        const copy = campaignNextStepCopy(row.id, {
                          spanish,
                          productName: product,
                          inbound: selected.channel === 'website',
                          items: readiness.items,
                        });
                        const seenTerms = new Set<CrmGlossaryTermId>();
                        return (
                          <li key={row.id} className="pursuit-next-step">
                            <div className="pursuit-next-step-head">
                              <strong>
                                <CrmTermText text={copy.title} spanish={spanish} seen={seenTerms} />
                              </strong>
                              <span>
                                <CrmTermText text={copy.where} spanish={spanish} seen={seenTerms} />
                              </span>
                            </div>
                            <div className="pursuit-next-step-how">
                              <CrmTermText text={copy.how} spanish={spanish} seen={seenTerms} />
                            </div>
                            <div className="pursuit-next-step-depends">
                              <CrmTermText text={copy.depends} spanish={spanish} seen={seenTerms} />
                            </div>
                            <div className="pursuit-next-step-actions">
                              {copy.actions.map((item) => (
                                <button
                                  key={item.label}
                                  type="button"
                                  className={item.primary ? 'dashboard-btn dashboard-btn-primary' : 'dashboard-btn'}
                                  onClick={() => runNextStep(item.action)}
                                >
                                  {item.label}
                                </button>
                              ))}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p role="status">
                      {spanish ? 'No hay pasos inmediatos abiertos.' : 'No immediate next steps are open.'}
                    </p>
                  )}
                  <div className="pursuit-next-step-docs">
                    {campaignNextStepSectionDocs(readiness.next[0]?.id, spanish).map((doc) =>
                      doc.external ? (
                        <a key={doc.href} href={doc.href} target="_blank" rel="noopener noreferrer">
                          {doc.label}
                        </a>
                      ) : (
                        <a
                          key={doc.href}
                          href={doc.href}
                          onClick={(event) => {
                            event.preventDefault();
                            openLaunchGuide(CAMPAIGN_LAUNCH_GUIDE_HASH[readiness.next[0]?.id || 'sku']);
                          }}
                        >
                          {doc.label}
                        </a>
                      ),
                    )}
                  </div>
                </div>
              </div>
              <div className={`pursuit-settings-accordion${settingsOpen ? ' is-open' : ''}`}>
                <div className="pursuit-settings-accordion-heading">
                  <button
                    type="button"
                    className="pursuit-settings-accordion-trigger"
                    aria-expanded={settingsOpen}
                    aria-controls="pursuit-campaign-settings"
                    onClick={() => setSettingsOpen((open) => !open)}
                  >
                    <span>{spanish ? 'Ajustes de campaña' : 'Campaign settings'}</span>
                    <em aria-hidden="true">{settingsOpen ? '▾' : '▸'}</em>
                  </button>
                  <HelpBadge label={spanish ? 'Acerca de los ajustes' : 'About campaign settings'}>
                    <p>
                      {spanish
                        ? 'Nombre, canal, estado, presupuesto, SKU y a quién vende. Permanece cerrado hasta que lo necesite. La descripción de la campaña está arriba.'
                        : 'Name, channel, status, budget, SKU, and who you sell to. Stays closed until you need it. The campaign description is above.'}
                    </p>
                  </HelpBadge>
                </div>
                {settingsOpen ? (
                  <div id="pursuit-campaign-settings" className="pursuit-settings-accordion-body">
              <label>
                {spanish ? 'Nombre' : 'Name'}
                <input value={draftName} onChange={(event) => setDraftName(event.target.value)} required />
              </label>
              <label>
                {spanish ? 'Canal' : 'Channel'}
                <select value={draftChannel} onChange={(event) => setDraftChannel(event.target.value)}>
                  {CAMPAIGN_CHANNELS.map((channel) => (
                    <option key={channel.value} value={channel.value}>
                      {channel.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Status
                <select value={draftStatus} onChange={(event) => setDraftStatus(event.target.value)}>
                  <option value="planned">{spanish ? 'Planificada' : 'Planned'}</option>
                  <option value="active">{spanish ? 'Activa' : 'Active'}</option>
                  <option value="paused">{spanish ? 'Pausada' : 'Paused'}</option>
                  <option value="completed">{spanish ? 'Completada' : 'Completed'}</option>
                </select>
              </label>
              <label>
                {spanish ? 'Presupuesto' : 'Budget'}
                <input type="number" min={0} value={draftBudget} onChange={(event) => setDraftBudget(event.target.value)} />
              </label>
              <label>
                <span>
                  {spanish ? 'SKU asociado' : 'Associated SKU'}
                  <CrmTermHelp term="sku" spanish={spanish} />
                </span>
                <select value={draftProduct} onChange={(event) => setDraftProduct(event.target.value)}>
                  <option value="">{spanish ? 'Ninguno' : 'None'}</option>
                  {products.map((row) => (
                    <option key={row.id} value={row.name}>
                      {row.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="pursuit-icp-description">
                <span>
                  {spanish ? 'A quién vende' : 'Who you sell to'}
                  <CrmTermHelp term="icp" spanish={spanish} />
                </span>
                <textarea
                  aria-label={spanish ? 'A quién vende' : 'Who you sell to'}
                  rows={4}
                  value={draftDescription}
                  onChange={(event) => setDraftDescription(event.target.value)}
                  placeholder={
                    spanish
                      ? 'A quién vende, qué pueden pagar, y opcionalmente lea sitio.com para contexto.'
                      : 'Who you sell to, what they can pay, and optionally read site.com. Build and edit the ICP matrix on Leads — saving here does not search for companies.'
                  }
                />
              </label>
              {pendingDelete ? (
                <div className="pursuit-confirm-bar" role="alertdialog" aria-labelledby="pursuit-campaign-delete-title">
                  <p id="pursuit-campaign-delete-title">
                    {spanish ? `¿Eliminar ${selected.name}?` : `Delete ${selected.name}?`}
                  </p>
                  <button
                    type="button"
                    className="dashboard-btn dashboard-btn-primary"
                    disabled={busy}
                    onClick={() =>
                      void runMutate(async () => {
                        await callApi(`/api/v1/pursuit/campaigns/${selected.id}`, { method: 'DELETE' });
                        setPendingDelete(false);
                        setSelectedId(null);
                      })
                    }
                  >
                    {spanish ? 'Eliminar' : 'Delete'}
                  </button>
                  <button type="button" className="dashboard-btn" onClick={() => setPendingDelete(false)}>
                    {spanish ? 'Cancelar' : 'Cancel'}
                  </button>
                </div>
              ) : (
                <div className="pursuit-project-editor-actions">
                  <button
                    type="button"
                    className="dashboard-btn dashboard-btn-primary"
                    disabled={busy || !draftName.trim()}
                    onClick={saveCampaign}
                  >
                    {spanish ? 'Guardar campaña' : 'Save campaign'}
                  </button>
                  <button type="button" className="dashboard-btn" disabled={busy} onClick={() => setPendingDelete(true)}>
                    {spanish ? 'Eliminar campaña' : 'Delete campaign'}
                  </button>
                </div>
              )}
                  </div>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="pursuit-empty">
              <p>
                {spanish
                  ? 'Elija una campaña para editar su audiencia.'
                  : 'Choose a campaign to edit its audience.'}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const PursuitHomePanel: React.FC<{
  spanish: boolean;
  busy: boolean;
  campaigns: Campaign[];
  products: Product[];
  leads: Lead[];
  opportunities: Opportunity[];
  overdueCount: number;
  missingNextCount: number;
  sequenceCount: number;
  openTaskCount: number;
  websiteLeadCount: number;
  quoteCount: number;
  setView: (view: string) => void;
  onOpenPipeline: (dealView?: string) => void;
  runMutate: (work: () => Promise<void>) => void;
  callApi: (path: string, init?: RequestInit) => Promise<unknown>;
  createFromForm: (path: string, body: Record<string, unknown>) => void;
  crmDataRevision: number;
  leadStats?: { by_campaign?: Record<string, number> };
}> = ({
  spanish,
  busy,
  campaigns,
  products,
  leads,
  opportunities,
  overdueCount,
  missingNextCount,
  sequenceCount,
  openTaskCount,
  websiteLeadCount,
  quoteCount,
  setView,
  onOpenPipeline,
  runMutate,
  callApi,
  createFromForm,
  crmDataRevision,
  leadStats,
}) => {
  const attention = [
    overdueCount
      ? {
          key: 'overdue',
          label: spanish
            ? `${overdueCount} ${overdueCount === 1 ? 'trato vencido' : 'tratos vencidos'}`
            : `${overdueCount} overdue ${overdueCount === 1 ? 'deal' : 'deals'}`,
          onClick: () => onOpenPipeline('overdue'),
        }
      : null,
    missingNextCount
      ? {
          key: 'next',
          label: spanish
            ? `${missingNextCount} sin siguiente acción`
            : `${missingNextCount} missing next action`,
          onClick: () => onOpenPipeline('missing_next'),
        }
      : null,
  ].filter(Boolean) as Array<{ key: string; label: string; onClick: () => void }>;

  return (
    <div className="pursuit-home">
      {attention.length ? (
        <ul className="pursuit-home-attention" aria-label={spanish ? 'Atención' : 'Needs attention'}>
          {attention.map((row) => (
            <li key={row.key}>
              <button type="button" className="is-warn" onClick={row.onClick}>
                {row.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <PursuitCampaignsPanel
        spanish={spanish}
        busy={busy}
        campaigns={campaigns}
        products={products}
        leads={leads}
        opportunities={opportunities}
        sequenceCount={sequenceCount}
        openTaskCount={openTaskCount}
        websiteLeadCount={websiteLeadCount}
        quoteCount={quoteCount}
        setView={setView}
        onOpenPipeline={onOpenPipeline}
        runMutate={runMutate}
        callApi={callApi}
        createFromForm={createFromForm}
        crmDataRevision={crmDataRevision}
        leadStats={leadStats}
      />
    </div>
  );
};

type InsightSnapshot = ReportPayload & {
  open_amount?: number;
  activity_count?: number;
  avg_sales_cycle_days?: number;
};

type InsightsWorkspace = {
  snapshot?: InsightSnapshot;
  dashboards?: Array<{ id: number; name: string }>;
  goals?: Array<{ id: number; name: string; progress?: { percent?: number } }>;
  reports?: Array<{ id: number; name: string; generated?: boolean; payload?: { summary?: string } }>;
  limits?: { dashboards?: number; goals?: number; reports?: number };
  metrics?: string[];
};

const PursuitInsightsPanel: React.FC<{
  spanish: boolean;
  busy: boolean;
  money: (value: number | null | undefined) => string;
  callApi: (path: string, init?: RequestInit) => Promise<unknown>;
  crmDataRevision: number;
}> = ({ spanish, busy, money, callApi, crmDataRevision }) => {
  const [workspace, setWorkspace] = React.useState<InsightsWorkspace | null>(null);
  const [section, setSection] = React.useState<'dashboards' | 'goals' | 'reports'>('dashboards');
  const [error, setError] = React.useState('');

  const load = React.useCallback(async () => {
    const next = (await callApi('/api/v1/pursuit/insights')) as InsightsWorkspace;
    setWorkspace(next);
  }, [callApi]);

  React.useEffect(() => {
    load().catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }, [load, crmDataRevision]);

  const run = (work: () => Promise<unknown>) => {
    setError('');
    work()
      .then(() => load())
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
  };

  const snapshot = workspace?.snapshot || {};
  const empty =
    (workspace?.dashboards || []).length === 0 &&
    (workspace?.goals || []).length === 0 &&
    (workspace?.reports || []).length === 0;

  return (
    <div className="pursuit-insights">
      <aside className="pursuit-insights-rail" aria-label={spanish ? 'Opciones de Insights' : 'Insights options'}>
        <button
          type="button"
          className="dashboard-btn"
          disabled={busy}
          onClick={() => run(() => callApi('/api/v1/pursuit/insights/dashboards', { method: 'POST', body: JSON.stringify({ name: spanish ? 'Resumen de ventas' : 'Sales overview' }) }))}
        >
          {spanish ? 'Crear tablero' : 'Create dashboard'}
        </button>
        {(
          [
            ['dashboards', spanish ? 'Tableros' : 'Dashboards', workspace?.dashboards?.length || 0, workspace?.limits?.dashboards || 25],
            ['goals', spanish ? 'Metas' : 'Goals', workspace?.goals?.length || 0, workspace?.limits?.goals || 50],
            ['reports', spanish ? 'Reportes' : 'Reports', workspace?.reports?.length || 0, workspace?.limits?.reports || 250],
          ] as const
        ).map(([id, label, count, limit]) => (
          <button key={id} type="button" className={section === id ? 'is-active' : ''} onClick={() => setSection(id)}>
            {label}
            <em>
              {count}/{limit}
            </em>
          </button>
        ))}
      </aside>
      <div className="pursuit-insights-main">
        {error ? <p className="banner">{error}</p> : null}
        {empty ? (
          <div className="pursuit-insights-empty">
            <h2>{spanish ? 'Identifique oportunidades de crecimiento. Actúe.' : 'Identify growth opportunities. Take action.'}</h2>
            <p>
              {spanish
                ? 'Tableros, metas y reportes del ciclo de ventas: primer contacto hasta cliente pagado. Incluidos en la suscripción independiente de Pursuit.'
                : 'Dashboards, goals, and reports of the sales cycle — first contact to paid customer. Included with a standalone Pursuit subscription.'}
            </p>
            <div className="pursuit-insights-actions">
              <button
                type="button"
                className="dashboard-btn"
                disabled={busy}
                onClick={() => run(() => callApi('/api/v1/pursuit/insights/dashboards', { method: 'POST', body: JSON.stringify({ name: 'Sales overview' }) }))}
              >
                {spanish ? 'Crear tablero' : 'Create dashboard'}
              </button>
              <button
                type="button"
                className="dashboard-btn dashboard-btn-secondary"
                disabled={busy}
                onClick={() => run(() => callApi('/api/v1/pursuit/insights/reports/generate', { method: 'POST' }))}
              >
                {spanish ? 'Generar reporte' : 'Generate report'}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="pursuit-forecast-row" aria-label="Insights">
              <div>
                <span>{spanish ? 'Pipeline abierto' : 'Open pipeline'}</span>
                <strong>{money(snapshot.open_amount)}</strong>
              </div>
              <div>
                <span>{spanish ? 'Ganado' : 'Won'}</span>
                <strong>{money(snapshot.won_amount)}</strong>
              </div>
              <div>
                <span>Win rate</span>
                <strong>{snapshot.win_rate || 0}%</strong>
              </div>
              <div>
                <span>{spanish ? 'Sin siguiente actividad' : 'No next activity'}</span>
                <strong>{snapshot.missing_next_action || 0}</strong>
              </div>
              <div>
                <span>{spanish ? 'Ciclo de ventas (días)' : 'Sales cycle (days)'}</span>
                <strong>{snapshot.avg_sales_cycle_days || 0}</strong>
              </div>
            </div>
            {section === 'goals' ? (
              <form
                className="pursuit-form pursuit-form-compact"
                onSubmit={(event) => {
                  event.preventDefault();
                  const form = new FormData(event.currentTarget);
                  run(() =>
                    callApi('/api/v1/pursuit/insights/goals', {
                      method: 'POST',
                      body: JSON.stringify({
                        name: String(form.get('name') || 'Won this month'),
                        metric: 'won_amount',
                        target: Number(form.get('target') || 0),
                        period: 'month',
                      }),
                    }),
                  );
                }}
              >
                <input name="name" placeholder={spanish ? 'Nombre de la meta' : 'Goal name'} defaultValue={spanish ? 'Ganado este mes' : 'Won this month'} />
                <input name="target" type="number" min={0} defaultValue={50000} />
                <button type="submit" className="dashboard-btn" disabled={busy}>
                  {spanish ? 'Añadir meta' : 'Add goal'}
                </button>
              </form>
            ) : null}
            {section === 'reports' ? (
              <button
                type="button"
                className="dashboard-btn dashboard-btn-secondary"
                disabled={busy}
                onClick={() => run(() => callApi('/api/v1/pursuit/insights/reports/generate', { method: 'POST' }))}
              >
                {spanish ? 'Generar reporte' : 'Generate report'}
              </button>
            ) : null}
            {(workspace?.reports || [])
              .filter((row) => row.generated)
              .map((row) => (
                <article key={row.id} className="pursuit-insight-card">
                  <h3>{row.name}</h3>
                  <p>{row.payload?.summary}</p>
                </article>
              ))}
          </>
        )}
      </div>
    </div>
  );
};

const PursuitTerritoriesPanel: React.FC<{
  spanish: boolean;
  busy: boolean;
  accounts: Account[];
  createFromForm: (path: string, body: Record<string, unknown>) => void;
  callApi: (path: string, init?: RequestInit) => Promise<unknown>;
  crmDataRevision: number;
}> = ({ spanish, busy, accounts, createFromForm, callApi, crmDataRevision }) => {
  const [rows, setRows] = React.useState<Array<{ id: number; name: string; region?: string | null }>>([]);
  React.useEffect(() => {
    let cancelled = false;
    void callApi('/api/v1/pursuit/territories')
      .then((payload) => {
        if (!cancelled && Array.isArray(payload)) setRows(payload);
      })
      .catch(() => {
        if (!cancelled) setRows([]);
      });
    return () => {
      cancelled = true;
    };
  }, [callApi, crmDataRevision]);
  return (
    <>
      <form
        className="pursuit-form"
        onSubmit={(event) => {
          event.preventDefault();
          const form = new FormData(event.currentTarget);
          createFromForm('/api/v1/pursuit/territories', {
            name: String(form.get('name') || '').trim(),
            region: String(form.get('region') || '').trim() || null,
          });
          event.currentTarget.reset();
        }}
      >
        <input name="name" placeholder={spanish ? 'Territorio' : 'Territory'} required />
        <input name="region" placeholder="AMER" />
        <button type="submit" className="dashboard-security-inline-btn" disabled={busy}>
          {spanish ? 'Añadir' : 'Add'}
        </button>
      </form>
      <table className="pursuit-table">
        <thead>
          <tr>
            <th>{spanish ? 'Nombre' : 'Name'}</th>
            <th>{spanish ? 'Región' : 'Region'}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td>{row.name}</td>
              <td>{row.region || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {accounts.length ? (
        <p className="muted">{spanish ? `${accounts.length} cuentas en el espacio.` : `${accounts.length} accounts in this workspace.`}</p>
      ) : null}
    </>
  );
};

const PursuitDealDeskPanel: React.FC<{
  spanish: boolean;
  money: (value: number | null | undefined) => string;
  callApi: (path: string, init?: RequestInit) => Promise<unknown>;
  crmDataRevision: number;
}> = ({ spanish, money, callApi, crmDataRevision }) => {
  const [desk, setDesk] = React.useState<{
    commit: Array<{ id: number; name: string; amount: number; product?: string | null }>;
    draft_quotes: Array<{ id: number; name: string; amount: number }>;
    missing_next_action: number;
  } | null>(null);
  React.useEffect(() => {
    let cancelled = false;
    void callApi('/api/v1/pursuit/handoffs/deal-desk')
      .then((payload) => {
        if (!cancelled && payload && typeof payload === 'object' && !Array.isArray(payload)) {
          setDesk(payload as typeof desk);
        }
      })
      .catch(() => {
        if (!cancelled) setDesk(null);
      });
    return () => {
      cancelled = true;
    };
  }, [callApi, crmDataRevision]);
  if (!desk) {
    return <p>{spanish ? 'Cargando mesa de deals…' : 'Loading deal desk…'}</p>;
  }
  return (
    <>
      <p>
        {spanish
          ? `${desk.missing_next_action} tratos sin siguiente acción.`
          : `${desk.missing_next_action} deals missing a next action.`}
      </p>
      <h3>{spanish ? 'Commit' : 'Commit'}</h3>
      <table className="pursuit-table">
        <thead>
          <tr>
            <th>{spanish ? 'Trato' : 'Deal'}</th>
            <th>{spanish ? 'Producto' : 'Product'}</th>
            <th>{spanish ? 'Monto' : 'Amount'}</th>
          </tr>
        </thead>
        <tbody>
          {desk.commit.length ? (
            desk.commit.map((row) => (
              <tr key={row.id}>
                <td>{row.name}</td>
                <td>{row.product || '—'}</td>
                <td>{money(row.amount)}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={3}>{spanish ? 'Ningún trato en commit.' : 'No commit-stage deals.'}</td>
            </tr>
          )}
        </tbody>
      </table>
      <h3>{spanish ? 'Cotizaciones en borrador' : 'Draft quotes'}</h3>
      <table className="pursuit-table">
        <thead>
          <tr>
            <th>{spanish ? 'Cotización' : 'Quote'}</th>
            <th>{spanish ? 'Monto' : 'Amount'}</th>
          </tr>
        </thead>
        <tbody>
          {desk.draft_quotes.length ? (
            desk.draft_quotes.map((row) => (
              <tr key={row.id}>
                <td>{row.name}</td>
                <td>{money(row.amount)}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={2}>{spanish ? 'Sin borradores.' : 'No drafts.'}</td>
            </tr>
          )}
        </tbody>
      </table>
    </>
  );
};

const PursuitExtraPanels: React.FC<Props> = ({
  view,
  spanish,
  busy,
  project,
  projects,
  products,
  campaigns,
  leads,
  quotes,
  tickets,
  sequences,
  enrollments,
  reports,
  accounts,
  opportunities,
  overdueCount,
  missingNextCount,
  openTaskCount,
  websiteLeadCount,
  enrollmentCount,
  money,
  accountName,
  setProject,
  setView,
  onOpenPipeline,
  createFromForm,
  runMutate,
  callApi,
  crmDataRevision,
  leadStats,
}) => {
  if (view === 'home') {
    return (
      <PursuitHomePanel
        spanish={spanish}
        busy={busy}
        campaigns={campaigns}
        products={products}
        leads={leads}
        opportunities={opportunities}
        overdueCount={overdueCount}
        missingNextCount={missingNextCount}
        sequenceCount={enrollmentCount}
        openTaskCount={openTaskCount}
        websiteLeadCount={websiteLeadCount}
        quoteCount={quotes.length}
        setView={setView}
        onOpenPipeline={onOpenPipeline}
        runMutate={runMutate}
        callApi={callApi}
        createFromForm={createFromForm}
        crmDataRevision={crmDataRevision}
        leadStats={leadStats}
      />
    );
  }

  if (view === 'guide') {
    return <CampaignLaunchGuidePanel spanish={spanish} onBack={() => setView('home')} />;
  }

  if (view === 'projects') {
    return (
      <PursuitProjectsPanel
        spanish={spanish}
        busy={busy}
        project={project}
        projects={projects}
        setProject={setProject}
        setView={setView}
        runMutate={runMutate}
        callApi={callApi}
      />
    );
  }

  if (view === 'products') {
    return (
      <>
        <h3>
          {spanish ? 'Productos' : 'Products'}
          <HelpBadge label={spanish ? 'Acerca de los productos' : 'About products'}>
            <p>
              {spanish
                ? 'Estos son SKUs. Filtre el pipeline por producto. No cree un espacio por plan.'
                : 'These are SKUs. Filter the pipeline by product. Do not create a workspace per plan.'}
            </p>
          </HelpBadge>
        </h3>
        <form
          className="pursuit-form"
          onSubmit={(event) => {
            event.preventDefault();
            const form = new FormData(event.currentTarget);
            createFromForm('/api/v1/pursuit/products', {
              name: String(form.get('name') || '').trim(),
              sku: String(form.get('sku') || '').trim() || null,
              family: String(form.get('family') || '').trim() || null,
              list_price: Number(form.get('list_price') || 0),
              billing_period: String(form.get('billing_period') || 'annual'),
            });
            event.currentTarget.reset();
          }}
        >
          <input name="name" placeholder={spanish ? 'Producto' : 'Product'} required />
          <span className="pursuit-field-with-help">
            <input name="sku" placeholder="SKU" aria-label="SKU" />
            <CrmTermHelp term="sku" spanish={spanish} />
          </span>
          <input name="family" placeholder={spanish ? 'Familia' : 'Family'} />
          <input name="list_price" type="number" min={0} placeholder="Price" />
          <select name="billing_period" defaultValue="annual">
            <option value="annual">Annual</option>
            <option value="monthly">Monthly</option>
            <option value="one_time">One-time</option>
          </select>
          <button type="submit" className="dashboard-security-inline-btn" disabled={busy}>
            {spanish ? 'Añadir producto' : 'Add product'}
          </button>
        </form>
        <table className="pursuit-table">
          <thead>
            <tr>
              <th>{spanish ? 'Nombre' : 'Name'}</th>
              <th>SKU</th>
              <th>{spanish ? 'Familia' : 'Family'}</th>
              <th>{spanish ? 'Precio' : 'Price'}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {products.map((row) => (
              <tr key={row.id}>
                <td>{row.name}</td>
                <td>{row.sku || '—'}</td>
                <td>{row.family || '—'}</td>
                <td>{money(row.list_price)}</td>
                <td>
                  <button
                    type="button"
                    className="dashboard-security-inline-btn"
                    disabled={busy}
                    onClick={() => void runMutate(async () => { await callApi(`/api/v1/pursuit/products/${row.id}`, { method: 'DELETE' }); })}
                  >
                    {spanish ? 'Eliminar' : 'Delete'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </>
    );
  }

  if (view === 'campaigns') {
    return (
      <PursuitCampaignsPanel
        spanish={spanish}
        busy={busy}
        campaigns={campaigns}
        products={products}
        leads={leads}
        opportunities={opportunities}
        sequenceCount={enrollmentCount}
        openTaskCount={openTaskCount}
        websiteLeadCount={websiteLeadCount}
        quoteCount={quotes.length}
        setView={setView}
        onOpenPipeline={onOpenPipeline}
        runMutate={runMutate}
        callApi={callApi}
        createFromForm={createFromForm}
        crmDataRevision={crmDataRevision}
        leadStats={leadStats}
      />
    );
  }

  if (view === 'quotes') {
    return (
      <>
        <form
          className="pursuit-form"
          onSubmit={(event) => {
            event.preventDefault();
            const form = new FormData(event.currentTarget);
            const productId = Number(form.get('product_id'));
            const product = products.find((row) => row.id === productId);
            createFromForm('/api/v1/pursuit/quotes', {
              opportunity_id: Number(form.get('opportunity_id')),
              name: String(form.get('name') || '').trim(),
              lines: product
                ? [{ product_id: product.id, description: product.name, quantity: 1, unit_price: product.list_price }]
                : [{ description: String(form.get('name') || 'Quote line'), quantity: 1, unit_price: 0 }],
            });
            event.currentTarget.reset();
          }}
        >
          <input name="name" placeholder={spanish ? 'Nombre de cotización' : 'Quote name'} required />
          <select name="opportunity_id" required defaultValue="">
            <option value="" disabled>
              {spanish ? 'Trato' : 'Deal'}
            </option>
            {opportunities.map((opp) => (
              <option key={opp.id} value={opp.id}>
                {opp.name}
              </option>
            ))}
          </select>
          <select name="product_id" defaultValue="">
            <option value="">{spanish ? 'Producto' : 'Product'}</option>
            {products.map((row) => (
              <option key={row.id} value={row.id}>
                {row.name} · {money(row.list_price)}
              </option>
            ))}
          </select>
          <button type="submit" className="dashboard-security-inline-btn" disabled={busy}>
            {spanish ? 'Crear cotización' : 'Create quote'}
          </button>
        </form>
        <table className="pursuit-table">
          <thead>
            <tr>
              <th>{spanish ? 'Cotización' : 'Quote'}</th>
              <th>Status</th>
              <th>{spanish ? 'Monto' : 'Amount'}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {quotes.map((row) => (
              <tr key={row.id}>
                <td>{row.name}</td>
                <td>{row.status}</td>
                <td>{money(row.amount)}</td>
                <td>
                  {row.status !== 'issued' && row.status !== 'accepted' ? (
                    <button
                      type="button"
                      className="dashboard-security-inline-btn"
                      disabled={busy}
                      onClick={() =>
                        void runMutate(async () => {
                          await callApi(`/api/v1/pursuit/quotes/${row.id}/issue`, { method: 'POST' });
                        })
                      }
                    >
                      {spanish ? 'Emitir' : 'Issue'}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="dashboard-security-inline-btn"
                    disabled={busy}
                    onClick={() => void runMutate(async () => { await callApi(`/api/v1/pursuit/quotes/${row.id}`, { method: 'DELETE' }); })}
                  >
                    {spanish ? 'Eliminar' : 'Delete'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </>
    );
  }

  if (view === 'tickets') {
    return (
      <>
        <form
          className="pursuit-form"
          onSubmit={(event) => {
            event.preventDefault();
            const form = new FormData(event.currentTarget);
            createFromForm('/api/v1/pursuit/tickets', {
              subject: String(form.get('subject') || '').trim(),
              priority: String(form.get('priority') || 'normal'),
              account_id: Number(form.get('account_id')) || null,
            });
            event.currentTarget.reset();
          }}
        >
          <input name="subject" placeholder={spanish ? 'Asunto' : 'Subject'} required />
          <select name="priority" defaultValue="normal">
            <option value="low">Low</option>
            <option value="normal">Normal</option>
            <option value="high">High</option>
          </select>
          <select name="account_id" defaultValue="">
            <option value="">{spanish ? 'Cuenta' : 'Account'}</option>
            {accounts.map((row) => (
              <option key={row.id} value={row.id}>
                {row.name}
              </option>
            ))}
          </select>
          <button type="submit" className="dashboard-security-inline-btn" disabled={busy}>
            {spanish ? 'Abrir ticket' : 'Open ticket'}
          </button>
        </form>
        <table className="pursuit-table">
          <thead>
            <tr>
              <th>{spanish ? 'Asunto' : 'Subject'}</th>
              <th>Status</th>
              <th>{spanish ? 'Prioridad' : 'Priority'}</th>
              <th>{spanish ? 'Cuenta' : 'Account'}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {tickets.map((row) => (
              <tr key={row.id}>
                <td>{row.subject}</td>
                <td>{row.status}</td>
                <td>{row.priority}</td>
                <td>{row.account_id ? accountName(row.account_id) : '—'}</td>
                <td>
                  {row.status !== 'closed' ? (
                    <button
                      type="button"
                      className="dashboard-security-inline-btn"
                      disabled={busy}
                      onClick={() =>
                        void runMutate(async () => {
                          await callApi(`/api/v1/pursuit/tickets/${row.id}`, {
                            method: 'PATCH',
                            body: JSON.stringify({ status: 'closed' }),
                          });
                        })
                      }
                    >
                      {spanish ? 'Cerrar' : 'Close'}
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </>
    );
  }

  if (view === 'sequences') {
    return (
      <>
        <form
          className="pursuit-form"
          onSubmit={(event) => {
            event.preventDefault();
            const form = new FormData(event.currentTarget);
            createFromForm('/api/v1/pursuit/sequences', {
              name: String(form.get('name') || '').trim(),
              channel: String(form.get('channel') || 'email'),
              steps: String(form.get('steps') || '').trim() || null,
            });
            event.currentTarget.reset();
          }}
        >
          <input name="name" placeholder={spanish ? 'Secuencia' : 'Sequence'} required />
          <select name="channel" defaultValue="email">
            <option value="email">Email</option>
            <option value="call">Call</option>
            <option value="mixed">Mixed</option>
          </select>
          <input name="steps" placeholder={spanish ? 'Pasos' : 'Steps'} />
          <button type="submit" className="dashboard-security-inline-btn" disabled={busy}>
            {spanish ? 'Añadir secuencia' : 'Add sequence'}
          </button>
        </form>
        <form
          className="pursuit-form"
          onSubmit={(event) => {
            event.preventDefault();
            const form = new FormData(event.currentTarget);
            void runMutate(async () => {
              await callApi('/api/v1/pursuit/handoffs/mailbox', {
                method: 'POST',
                body: JSON.stringify({
                  from: String(form.get('from') || '').trim(),
                  subject: String(form.get('subject') || '').trim(),
                  body: String(form.get('body') || '').trim() || null,
                  provider: 'gmail',
                }),
              });
            });
            event.currentTarget.reset();
          }}
        >
          <input name="from" type="email" placeholder={spanish ? 'De (email)' : 'From (email)'} required />
          <input name="subject" placeholder={spanish ? 'Asunto' : 'Subject'} required />
          <input name="body" placeholder={spanish ? 'Cuerpo' : 'Body'} />
          <button type="submit" className="dashboard-security-inline-btn" disabled={busy}>
            {spanish ? 'Registrar correo' : 'Log email'}
          </button>
        </form>
        <p>
          <button
            type="button"
            className="dashboard-security-inline-btn"
            disabled={busy}
            onClick={() =>
              void runMutate(async () => {
                await callApi('/api/v1/pursuit/handoffs/sequence-tick', { method: 'POST' });
              })
            }
          >
            {spanish ? 'Avanzar nurtures' : 'Advance nurtures'}
          </button>
        </p>
        <table className="pursuit-table">
          <thead>
            <tr>
              <th>{spanish ? 'Nombre' : 'Name'}</th>
              <th>{spanish ? 'Canal' : 'Channel'}</th>
              <th>{spanish ? 'Pasos' : 'Steps'}</th>
            </tr>
          </thead>
          <tbody>
            {sequences.map((row) => (
              <tr key={row.id}>
                <td>{row.name}</td>
                <td>{row.channel}</td>
                <td>{row.steps || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {enrollments.length ? (
          <table className="pursuit-table">
            <thead>
              <tr>
                <th>Enrollment</th>
                <th>Status</th>
                <th>{spanish ? 'Paso' : 'Step'}</th>
              </tr>
            </thead>
            <tbody>
              {enrollments.map((row) => (
                <tr key={row.id}>
                  <td>#{row.id} → seq {row.sequence_id}</td>
                  <td>{row.status}</td>
                  <td>{row.step_index ?? 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </>
    );
  }

  if (view === 'territories') {
    return (
      <PursuitTerritoriesPanel
        spanish={spanish}
        busy={busy}
        accounts={accounts}
        createFromForm={createFromForm}
        callApi={callApi}
        crmDataRevision={crmDataRevision}
      />
    );
  }

  if (view === 'desk') {
    return <PursuitDealDeskPanel spanish={spanish} money={money} callApi={callApi} crmDataRevision={crmDataRevision} />;
  }

  if (view === 'insights' || view === 'reports') {
    return (
      <PursuitInsightsPanel
        spanish={spanish}
        busy={busy}
        money={money}
        callApi={callApi}
        crmDataRevision={crmDataRevision}
      />
    );
  }

  return null;
};

export default PursuitExtraPanels;
